import type { MenuContext, MenuScreen, Opponent } from '../types.js';
import type { ActiveSession } from '../../history/types.js';
import { createScreenShell, createMenuHeader, createCrestButton, el } from '@bn-games/ui-shell';
import {
  modeOpponentLabel,
  type ModeOpponentStrings,
} from '@bn-games/history-core/ui';
import { t } from '../../i18n/I18n.js';
import { isDiscordActivity } from '@bn-games/discord-activity';

function modeOpponentStrings(): ModeOpponentStrings {
  return {
    // Plain mode label only — difficulty intentionally dropped so the resume
    // button stays the same size as the other buttons in the stack.
    vsAi: () => t('playMenu.vsAi'),
    local: t('playMenu.localMode'),
    online: (opponent) => t('playMenu.onlineMode', { opponent }),
    opponent: t('common.opponent'),
  };
}

function sessionLabel(session: ActiveSession): string {
  return modeOpponentLabel(
    {
      mode: session.seed.mode,
      opponentName: session.seed.playerBlack || session.seed.playerWhite,
    },
    modeOpponentStrings(),
  );
}

type LaunchTarget =
  | { kind: 'local'; opponent: Opponent }
  | { kind: 'online-create' }
  | { kind: 'online-join' };

export function createPlayMenuScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  function launch(ctx: MenuContext, target: LaunchTarget): void {
    const go = (): void => {
      if (target.kind === 'local') {
        ctx.setOpponent(target.opponent);
        ctx.navigate('game');
      } else if (target.kind === 'online-create') {
        ctx.setOpponent('online');
        ctx.navigate('online-create');
      } else {
        ctx.setOpponent('online');
        ctx.navigate('online-join');
      }
    };
    ctx.discardActiveSession(go);
  }

  function paint(root: HTMLElement, ctx: MenuContext): void {
    while (root.firstChild) root.removeChild(root.firstChild);
    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;
    body.style.justifyContent = 'space-between';

    body.appendChild(createMenuHeader({ title: t('playMenu.title') }));

    const stack: HTMLElement[] = [];

    // Inside Discord the instance room is the only online room that exists:
    // room codes cannot be exchanged in the iframe, and resuming a room saved
    // by a previous web session would pull the player out of the instance.
    const roomCodeLobby = !isDiscordActivity();

    const session = ctx.getActiveSession();
    const savedOnline = roomCodeLobby ? ctx.getSavedSession() : null;

    if (session) {
      stack.push(
        createCrestButton({
          label: `${t('playMenu.resume')} · ${sessionLabel(session)}`,
          primary: true,
          onClick: () => ctx.resumeActiveGame(),
        }),
      );
    } else if (savedOnline) {
      stack.push(
        createCrestButton({
          label: t('playMenu.resumeOnline', { code: savedOnline.roomCode }),
          primary: true,
          onClick: () => {
            ctx.setOpponent('online');
            ctx.resumeSession();
          },
        }),
      );
    }

    const hasResumeCta = Boolean(session || savedOnline);

    stack.push(
      createCrestButton({
        label: t('playMenu.vsAi'),
        primary: !hasResumeCta,
        onClick: () => launch(ctx, { kind: 'local', opponent: 'ai' }),
      }),
    );
    stack.push(
      createCrestButton({
        label: t('playMenu.vsHuman'),
        onClick: () => launch(ctx, { kind: 'local', opponent: 'local' }),
      }),
    );
    if (roomCodeLobby) {
      stack.push(
        createCrestButton({
          label: t('playMenu.createOnline'),
          onClick: () => launch(ctx, { kind: 'online-create' }),
        }),
      );
      stack.push(
        createCrestButton({
          label: t('playMenu.joinOnline'),
          onClick: () => launch(ctx, { kind: 'online-join' }),
        }),
      );
    }

    const buttons = el('div', { class: 'button-stack' }, stack);

    const back = el('div', { class: 'button-row back-button-row' }, [
      createCrestButton({ label: t('common.back'), small: true, onClick: () => ctx.navigate('menu') }),
    ]);

    const center = el('div', { class: 'about-center' }, [buttons]);

    body.appendChild(center);
    body.appendChild(back);
    root.appendChild(frame);
  }

  return {
    mount(root, ctx) {
      paint(root, ctx);
    },
    update(ctx) {
      if (frame?.parentElement) paint(frame.parentElement, ctx);
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
